import React, { useState, useEffect } from 'react';
import { useThrottleFn, useMemoizedFn } from 'ahooks';
import useCalendarCardSize from './useCalendarCardSize';
import { getCalendarColCfgMapping } from '../utils';
import { DEFAULT_CALENDAR_COL_SPACING, DEFAULT_CALENDAR_CARD_SPACING } from '../constants';

import type { ICalendarColCfgMapping } from '../types';

export default function useCalendarCardLayout(props: {
  // 日历卡片外层容器（支持滚动那个dom）
  cardListEl?: React.RefObject<HTMLElement>;
  // 是否正在加载日历数据
  isLoading?: boolean;
  // 是否无日历数据
  isNoData?: boolean;
  // 日历数据
  calendarDataList?: CommonCalendarDataTypes;
  // 重新计算的依赖
  deps: React.DependencyList;
}) {
  const { cardListEl, isLoading = true, isNoData = true, calendarDataList, deps } = props;

  const {
    // 日历卡片的尺寸
    colSpacing,
    // 列间距
    cardSpacing,
    // 日历卡片的宽度
    cardWidth,
    // 日历卡片尺寸change回调
    onCardSizeChange,
    // 重置日历卡片尺寸为默认尺寸
    resetCardSize,
  } = useCalendarCardSize();

  // 列与列配置的映射（键为列，即列对应的日期，值为该列的配置，包含列的宽度信息）
  const [colCfgMapping, setColCfgMapping] = useState<ICalendarColCfgMapping>({});

  /**
   * 获取列的marginRight
   * 最后一列marginRight为0，否则为colSpacing
   * @param index 列索引
   * @return 列的marginRight
   */
  const getCalendarColMR = useMemoizedFn((index?: number): number => {
    if (typeof index !== 'number') return DEFAULT_CALENDAR_COL_SPACING;
    if (!Array.isArray(calendarDataList) || !calendarDataList.length) return 0;
    return index === calendarDataList.length - 1 ? 0 : colSpacing;
  });

  /**
   * 计算日历卡片的marginRight值，默认为DEFAULT_CALENDAR_CARD_SPACING
   * 每一列的每一行的最后一个marginRight为0，其余为cardSpacing指定的值
   * @param colKey 列键
   * @param index 日历卡片索引
   * @returns 日历卡片的marginRight值（每一列的每一行的最后一个marginRight为0，其余为cardSpacing指定的值）
   */
  const getCalendarCardMR = useMemoizedFn((colKey?: string, index?: number): number => {
    if (!colKey || typeof index !== 'number') return DEFAULT_CALENDAR_CARD_SPACING;
    // 列配置
    const colCfg = colCfgMapping[colKey];
    // 列每一行展示的日历卡片数量
    const cardsNumPerRow = colCfg?.cardsNumPerRow || 0;
    // 每一列的每一行仅展示一个日历卡片时，日历卡片的marginRight为0
    if (cardsNumPerRow <= 1) return 0;
    /**
     * 列每一行最后一个日历卡片marginRight为0
     * 否则日历卡片marginRight为cardSpacing
     */
    return index % cardsNumPerRow === cardsNumPerRow - 1 ? 0 : cardSpacing;
  });

  const handleResize = () => {
    const cardListElTmp = cardListEl?.current;
    if (isLoading || isNoData || !cardListElTmp) {
      setColCfgMapping({});
      return;
    }
    const colCfgMappingTmp = getCalendarColCfgMapping({
      cardListEl: cardListElTmp,
      calendarDataList,
      colSpacing,
      cardSpacing,
      cardWidth,
    });
    setColCfgMapping(colCfgMappingTmp);
  };

  const { run: handleResizeRun } = useThrottleFn(handleResize, {
    wait: 50,
    leading: true,
    trailing: true,
  });

  useEffect(() => {
    handleResize();

    window.addEventListener('resize', handleResizeRun, false);

    return () => {
      window.removeEventListener('resize', handleResizeRun, false);
    };
  }, [colSpacing, cardSpacing, cardWidth, deps]);

  return {
    // 列间距
    colSpacing,
    // 列配置映射（键为列对应的日期，值为列信息）
    colCfgMapping,
    // 获取列的marginRight
    getCalendarColMR,
    // 获取日历卡片的marginRight
    getCalendarCardMR,
    // 日历卡片尺寸change回调
    onCardSizeChange,
    // 重置日历卡片尺寸为默认尺寸
    resetCardSize,
  };
}
